import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../layout/Layout.js';
import Section, { SectionTitle } from '../../common/Section.js';
import Button from '../../common/Button.js';
import styled from 'styled-components';

const BlogContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  
  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
    margin-bottom: 2rem;
  }
  
  .meta {
    display: flex;
    gap: 1rem;
    font-size: 0.875rem;
    color: var(--gray);
    margin-bottom: 1rem;
    
    .category {
      color: var(--secondary);
      font-weight: 600;
    }
  }
  
  h1 {
    margin-bottom: 1.5rem;
  }
  
  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
  }
  
  .back-button {
    margin-top: 3rem;
  }
`;

const BlogPostPage = () => {
  const { id } = useParams();
  const postId = parseInt(id, 10);
  
  // Blog post data (this would normally come from an API or CMS)
  const posts = [
    {
      id: 1,
      title: "7 Things to Look for When Buying a New Home",
      date: "March 15, 2023",
      category: "Buying Tips",
      image: "/images/blog1.png",
      content: [
        "When it comes to purchasing a new home, there are several crucial factors that every buyer should consider to ensure they're making a sound investment that will meet their needs for years to come.",
        "First, evaluate the location carefully. Consider proximity to work, schools, shopping, and healthcare facilities. A convenient location not only enhances your quality of life but also contributes significantly to property value over time.",
        "Second, assess the home's size and layout. Does it accommodate your current and future needs? Look for versatile spaces that can adapt as your family's requirements change.",
        "Third, examine the structural integrity of the home. Pay special attention to the foundation, roof, plumbing, and electrical systems. These components are costly to repair and can cause significant problems if neglected.",
        "Fourth, consider energy efficiency. Features like quality insulation, energy-efficient windows, and modern HVAC systems can substantially reduce utility costs while providing better comfort.",
        "Fifth, evaluate storage space. Adequate closets, cabinets, and possibly an attic or basement can make a significant difference in your day-to-day living experience.",
        "Sixth, check the outdoor space. Whether it's a yard for children and pets, a garden area, or simply a place for outdoor relaxation, the exterior spaces contribute significantly to your home's usability and value.",
        "Finally, consider the neighborhood's future developments. Research any planned construction or zoning changes that might impact your property's value or your quality of life.",
        "At Shoffner Homes, we consider all these factors when designing and building our custom homes, ensuring that each property offers the perfect balance of aesthetics, functionality, and value."
      ]
    },
    {
      id: 2,
      title: "Understanding the Home Building Process",
      date: "February 28, 2023",
      category: "Construction",
      image: "/images/blog2.png",
      content: [
        "Building a custom home is an exciting journey, but understanding the process can help make it smoother and more enjoyable. Here's a comprehensive guide to the home construction process from start to finish.",
        "The process begins with planning and design. This crucial phase involves working with architects and designers to create a home that meets your needs, preferences, and budget. Take time to communicate your vision clearly, including must-have features and nice-to-have elements.",
        "Next comes the permitting phase. Before breaking ground, builders must obtain various permits from local authorities. This ensures compliance with building codes, zoning regulations, and other requirements. While this administrative step may seem tedious, it's essential for a lawful and sound construction.",
        "Once permits are secured, site preparation begins. This involves clearing the land, excavating where necessary, and preparing the foundation. The foundation is arguably the most critical component of your home, as it supports the entire structure and must be done correctly.",
        "With a solid foundation in place, framing begins. This is when your home starts taking shape, with the skeleton of walls, floors, and roof being erected. It's an exciting time as you can begin to visualize the actual spaces within your home.",
        "After framing comes the installation of core systems: plumbing, electrical, and HVAC. These hidden components are vital to your home's functionality and comfort. Quality work at this stage prevents costly issues down the line.",
        "Next, insulation and drywall are installed, creating the interior walls of your home. This is followed by interior finishes such as flooring, cabinetry, countertops, and fixtures. These elements contribute significantly to your home's aesthetic appeal and functionality.",
        "As interior work progresses, exterior finishes are applied. This includes siding, roofing, windows, and doors, which not only enhance curb appeal but also protect your home from the elements.",
        "The final stages involve finishing touches such as painting, trim work, and installation of appliances. This is followed by thorough inspections to ensure everything meets code requirements and quality standards.",
        "Finally, you'll conduct a walkthrough with your builder to identify any items that need attention before closing. Once these are addressed, you'll receive the keys to your new custom home.",
        "At Shoffner Homes, we guide our clients through each step, ensuring transparency, quality craftsmanship, and a finished product that exceeds expectations."
      ]
    },
    {
      id: 3,
      title: "Investment Opportunities in NC Triad Real Estate",
      date: "January 12, 2023",
      category: "Investment",
      image: "/images/blog3.png",
      content: [
        "The North Carolina Triad region, encompassing Greensboro, Winston-Salem, and High Point, offers compelling real estate investment opportunities. With its diverse economy, quality of life, and strategic location, the Triad continues to attract attention from savvy investors.",
        "One of the Triad's most appealing aspects is its affordability compared to larger metropolitan areas. This creates an attractive entry point for investors, with the potential for substantial appreciation as the region grows.",
        "The area's strong economic foundation supports a healthy real estate market. Major industries include healthcare, education, manufacturing, and a growing technology sector. This diverse economic base provides stability and resilience even during challenging economic times.",
        "Population growth trends favor real estate investment. The Triad has experienced steady growth, attracting both young professionals and retirees with its combination of urban amenities and natural beauty. This population influx creates consistent demand for housing.",
        "For investors, several strategies can yield attractive returns in the Triad market. Residential rental properties perform well, particularly in neighborhoods near universities, major employers, and popular amenities. Multi-family properties offer efficiency of scale and consistent cash flow.",
        "The single-family home market presents opportunities for both long-term appreciation and shorter-term fix-and-flip projects. The Triad's varied housing stock includes everything from historic homes to new construction, allowing investors to find properties that match their investment strategy and budget.",
        "Commercial real estate also offers potential, particularly in growing sectors like healthcare, services, and mixed-use developments. The trend toward flexible workspaces and e-commerce has created unique commercial investment opportunities.",
        "Land development represents another avenue for investment, particularly in the expanding suburbs and corridors between the Triad's major cities. As population growth continues, the demand for well-planned residential communities increases.",
        "When considering real estate investment in the Triad, it's essential to conduct thorough due diligence. This includes researching local market trends, understanding municipal development plans, and working with professionals familiar with the area's real estate dynamics.",
        "At Shoffner Homes, we not only build quality homes but also possess deep knowledge of the Triad real estate market. We're happy to share insights with investors looking to capitalize on the region's potential."
      ]
    }
  ];
  
  // Find the post that matches the ID
  const post = posts.find(p => p.id === postId);
  
  if (!post) {
    return (
      <Layout>
        <Section>
          <div className="container">
            <div style={{ textAlign: 'center' }}>
              <h2>Blog Post Not Found</h2>
              <p>The blog post you're looking for doesn't exist.</p>
              <Button as={Link} to="/blog" variant="secondary">
                Back to Blog
              </Button>
            </div>
          </div>
        </Section>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <Section>
        <div className="container">
          <BlogContent>
            <img src={post.image} alt={post.title} />
            <div className="meta">
              <span className="date">{post.date}</span>
              <span className="category">{post.category}</span>
            </div>
            <h1>{post.title}</h1>
            
            {post.content.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
            
            <div className="back-button">
              <Button as={Link} to="/blog" variant="secondary">
                Back to Blog
              </Button>
            </div>
          </BlogContent>
        </div>
      </Section>
    </Layout>
  );
};

export default BlogPostPage;